import React, { useMemo, useState } from 'react';
import {
  PlayCircle,
  Square,
  Terminal,
  GitBranch,
  Repeat,
  Zap,
  Timer,
  Variable,
  CheckCircle,
  Package,
  FileText,
  Mic,
  Bot,
  Activity,
  Search,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui';

interface NodeTypeItem {
  type: string;
  label: string;
  icon: React.ReactNode;
  description: string;
  color: string;
  group: 'flow' | 'logic' | 'data' | 'modem';
}

const NODE_TYPES: NodeTypeItem[] = [
  { type: 'start', label: 'Start', icon: <PlayCircle className="w-4 h-4" />, description: 'Flow entry point', color: 'text-status-online', group: 'flow' },
  { type: 'end', label: 'End', icon: <Square className="w-4 h-4" />, description: 'Flow exit point', color: 'text-accent-red', group: 'flow' },
  { type: 'primitiveCall', label: 'Primitive Call', icon: <Package className="w-4 h-4" />, description: 'Call reusable primitive', color: 'text-category-voice', group: 'flow' },

  { type: 'condition', label: 'Condition', icon: <GitBranch className="w-4 h-4" />, description: 'Conditional branch', color: 'text-accent-yellow', group: 'logic' },
  { type: 'loop', label: 'Loop', icon: <Repeat className="w-4 h-4" />, description: 'Repeat actions', color: 'text-category-diagnostics', group: 'logic' },
  { type: 'parallel', label: 'Parallel', icon: <Zap className="w-4 h-4" />, description: 'Execute in parallel', color: 'text-accent-indigo', group: 'logic' },
  { type: 'delay', label: 'Delay', icon: <Timer className="w-4 h-4" />, description: 'Wait for duration', color: 'text-muted-foreground', group: 'logic' },
  { type: 'assertion', label: 'Assertion', icon: <CheckCircle className="w-4 h-4" />, description: 'Validate conditions', color: 'text-accent-green', group: 'logic' },

  { type: 'variable', label: 'Variable', icon: <Variable className="w-4 h-4" />, description: 'Store/retrieve data', color: 'text-category-data', group: 'data' },
  { type: 'log', label: 'Log', icon: <FileText className="w-4 h-4" />, description: 'Output debug info', color: 'text-accent-blue', group: 'data' },

  { type: 'atCommand', label: 'AT Command', icon: <Terminal className="w-4 h-4" />, description: 'Send AT command', color: 'text-accent-blue', group: 'modem' },
  { type: 'dataTest', label: 'Data Test', icon: <Activity className="w-4 h-4" />, description: 'Ping / HTTP / DNS / SFTP …', color: 'text-category-data', group: 'modem' },
  { type: 'voiceTest', label: 'Voice Test', icon: <Mic className="w-4 h-4" />, description: 'Voice + bearer + DTMF', color: 'text-category-voice', group: 'modem' },
  { type: 'voiceAgentTest', label: 'AI Agent Call', icon: <Bot className="w-4 h-4" />, description: 'AI drives a live call (IVR / DTMF / PIN)', color: 'text-category-voice', group: 'modem' },
];

const GROUP_LABELS: Record<NodeTypeItem['group'], string> = {
  flow: 'Flow',
  logic: 'Logic',
  data: 'Data',
  modem: 'Modem (Primitives only)',
};

interface NodePaletteProps {
  isPrimitive?: boolean;
}

export function NodePalette({ isPrimitive = false }: NodePaletteProps) {
  const [search, setSearch] = useState('');

  const visibleNodes = useMemo(() => {
    // Scenarios cannot directly use AT Command or Voice Test — only primitives can.
    const base = isPrimitive ? NODE_TYPES : NODE_TYPES.filter((n) => n.group !== 'modem');
    if (!search.trim()) return base;
    const q = search.trim().toLowerCase();
    return base.filter(
      (n) => n.label.toLowerCase().includes(q) || n.description.toLowerCase().includes(q) || n.type.toLowerCase().includes(q)
    );
  }, [isPrimitive, search]);

  const grouped = useMemo(() => {
    const map = new Map<NodeTypeItem['group'], NodeTypeItem[]>();
    for (const node of visibleNodes) {
      const arr = map.get(node.group) ?? [];
      arr.push(node);
      map.set(node.group, arr);
    }
    return Array.from(map.entries());
  }, [visibleNodes]);

  const onDragStart = (event: React.DragEvent, nodeType: string) => {
    event.dataTransfer.setData('application/reactflow', nodeType);
    event.dataTransfer.effectAllowed = 'move';
  };

  return (
    <div className="h-full flex flex-col">
      {/* Sticky header */}
      <div className="sticky top-0 z-10 bg-card border-b border-border dark:border-border-subtle px-3 py-2 flex-shrink-0">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Nodes</h3>
          {isPrimitive && (
            <span className="text-[10px] text-status-warning uppercase tracking-wide">Primitive</span>
          )}
        </div>
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground/60" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search nodes…"
            className="h-8 pl-7 text-[13px]"
          />
        </div>
      </div>

      {/* Node list */}
      <div className="flex-1 overflow-y-auto p-2 space-y-3">
        {grouped.length === 0 ? (
          <p className="text-xs text-muted-foreground px-2 py-4 text-center">No nodes match "{search}".</p>
        ) : (
          grouped.map(([group, nodes]) => (
            <div key={group}>
              <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground px-2 mb-1">{GROUP_LABELS[group]}</div>
              <div className="space-y-1">
                {nodes.map((node) => (
                  <div
                    key={node.type}
                    draggable
                    onDragStart={(e) => onDragStart(e, node.type)}
                    title={node.description}
                    className={cn(
                      'group flex items-center gap-2 px-2 py-1.5 rounded-md cursor-move',
                      'border border-transparent hover:border-border hover:bg-muted/40',
                      'transition-colors',
                      'active:scale-[0.98]'
                    )}
                  >
                    <div className={cn('flex-shrink-0', node.color)}>{node.icon}</div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-medium text-foreground truncate">{node.label}</div>
                      <div className="text-[11px] text-muted-foreground truncate">{node.description}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
